import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useChurch } from '../context/ChurchContext';
import toast from 'react-hot-toast';
import useGlobalView from '../hooks/useGlobalView';
import ChurchBadge from '../components/common/ChurchBadge';
import { requiredMessage, apiFieldErrors, firstErrorMessage, FieldError, Req, ERROR_COLOR } from '../utils/formErrors';

const CARD = {
  background: 'var(--card-bg)', borderRadius: '14px', border: '1px solid var(--border)',
  boxShadow: 'var(--card-shadow)', padding: '22px', marginBottom: '20px',
};
const LABEL = { display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: '6px', color: 'var(--text-primary)' };
const INPUT = { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '0.88rem', background: '#fff', color: 'var(--text-primary)', boxSizing: 'border-box' };

const STATUS_LABELS = {
  nouveau: 'Nouveau', membre: 'Membre régulier', serviteur: 'Serviteur', invite: 'Invité',
};

function FamilyMembersModal({ family, churchId, onClose, onChanged }) {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(null);

  const fetchMembers = useCallback(() => {
    setLoading(true);
    api.get(`/participants/families/${family.id}/members/`)
      .then(res => setMembers(res.data))
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  }, [family.id]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  useEffect(() => {
    if (!search.trim()) { setResults([]); return; }
    setSearching(true);
    const timer = setTimeout(() => {
      api.get('/participants/', { params: { church_id: churchId, search, page_size: 10 } })
        .then(res => {
          const list = Array.isArray(res.data) ? res.data : res.data.results || [];
          setResults(list.filter(p => !members.some(m => m.id === p.id)));
        })
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [search, churchId, members]);

  const assign = async (participant) => {
    setAdding(participant.id);
    try {
      await api.patch(`/participants/${participant.id}/`, { family: family.id });
      toast.success(`${participant.full_name} ajouté(e) à ${family.name}.`);
      setSearch('');
      setResults([]);
      fetchMembers();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.family?.[0] || "Erreur lors de l'ajout.");
    } finally {
      setAdding(null);
    }
  };

  const remove = async (participant) => {
    if (!window.confirm(`Retirer ${participant.full_name} de "${family.name}" ?`)) return;
    try {
      await api.patch(`/participants/${participant.id}/`, { family: null });
      toast.success('Retiré de la famille.');
      fetchMembers();
      onChanged();
    } catch {
      toast.error('Erreur lors du retrait.');
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--card-bg)', borderRadius: '14px', width: '100%', maxWidth: '520px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)' }}>{family.name}</h2>
            <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{members.length} membre{members.length > 1 ? 's' : ''}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: 'var(--text-secondary)' }}>×</button>
        </div>

        {/* Sans église sélectionnée (vue globale) : consultation seule */}
        {churchId && <div style={{ padding: '16px 24px 12px', borderBottom: '1px solid var(--border)' }}>
          <label style={{ ...LABEL, marginBottom: '6px' }}>Ajouter un fidèle à cette famille</label>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher par nom..." style={INPUT} />
          {searching && <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '6px 0 0' }}>Recherche...</p>}
          {results.length > 0 && (
            <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '160px', overflowY: 'auto' }}>
              {results.map(p => (
                <button key={p.id} onClick={() => assign(p)} disabled={adding === p.id}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: '#fff', cursor: 'pointer', textAlign: 'left' }}>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>{p.full_name}</span>
                  <span style={{ fontSize: '0.76rem', color: '#7c3aed', fontWeight: 700 }}>{adding === p.id ? '...' : '+ Ajouter'}</span>
                </button>
              ))}
            </div>
          )}
        </div>}

        <div style={{ padding: '16px 24px', overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Chargement...</p>
          ) : members.length === 0 ? (
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Aucun membre dans cette famille.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {members.map(m => (
                <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                  <button onClick={() => navigate(`/participants/${m.id}`)}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>
                    <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1e40af', textDecoration: 'underline' }}>{m.full_name}</span>
                    <span style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', marginLeft: '8px' }}>{STATUS_LABELS[m.status] || m.status}</span>
                  </button>
                  {churchId && <button onClick={() => remove(m)} style={{ background: 'none', border: 'none', color: '#b91c1c', fontSize: '0.76rem', fontWeight: 700, cursor: 'pointer' }}>
                    Retirer
                  </button>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FamilyStatsPanel({ churchId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!churchId) return;
    setLoading(true);
    api.get('/participants/families/stats/', { params: { church_id: churchId } })
      .then(res => setStats(res.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [churchId]);

  if (loading) return <div style={CARD}><p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Chargement des statistiques...</p></div>;
  if (!stats || stats.total_families === 0) return null;

  const maxAttendance = Math.max(...stats.families.map(f => f.avg_attendance_per_member), 1);

  return (
    <div style={CARD}>
      <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px' }}>📊 Statistiques par famille</h2>
      <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0 0 16px' }}>
        {stats.total_families} famille{stats.total_families > 1 ? 's' : ''} · {stats.total_members_with_family} membre{stats.total_members_with_family > 1 ? 's' : ''} rattaché{stats.total_members_with_family > 1 ? 's' : ''} · présences des 90 derniers jours
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {stats.families.map(f => (
          <div key={f.family_id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '4px' }}>
              <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{f.family_name}</span>
              <span style={{ color: 'var(--text-secondary)' }}>{f.member_count} membre{f.member_count > 1 ? 's' : ''} ({f.member_percent}%) · {f.attendance_count_90d} présences</span>
            </div>
            <div style={{ height: '8px', borderRadius: '999px', background: '#f1f5f9', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${(f.avg_attendance_per_member / maxAttendance) * 100}%`, background: '#7c3aed', borderRadius: '999px' }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Families() {
  const { user } = useAuth();
  const { activeChurchId, isPlatformAdmin, canSwitchChurch } = useChurch();
  const churchId = canSwitchChurch ? activeChurchId : user?.church_id;
  const isGlobal = useGlobalView();

  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [useFamilies, setUseFamilies] = useState(null);
  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewingFamily, setViewingFamily] = useState(null);

  const fetchSettings = useCallback(async () => {
    if (!churchId) { setUseFamilies(false); return; }
    try {
      const res = await api.get('/notifications/church-settings/', { params: { church_id: churchId } });
      setUseFamilies(res.data.use_families ?? false);
    } catch {
      setUseFamilies(false);
    }
  }, [churchId]);

  const fetchFamilies = useCallback(async () => {
    if (!churchId && !isGlobal) { setFamilies([]); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await api.get('/participants/families/', { params: { church_id: churchId } });
      setFamilies(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch {
      setFamilies([]);
    } finally {
      setLoading(false);
    }
  }, [churchId, isGlobal]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => { fetchFamilies(); }, [fetchFamilies]);

  // Comptes « Chef de Famille » de l'église, pour désigner le chef de chaque famille (RCC-RAP-001)
  const [leaders, setLeaders] = useState([]);
  useEffect(() => {
    if (!churchId) { setLeaders([]); return; }
    api.get('/auth/users/', { params: { church_id: churchId, page_size: 500 }, silentError: true })
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : res.data.results || [];
        setLeaders(list.filter(u => u.role === 'chef_famille'));
      })
      .catch(() => setLeaders([]));
  }, [churchId]);

  const setLeader = async (family, leaderId) => {
    try {
      await api.patch(`/participants/families/${family.id}/`, { leader: leaderId ? Number(leaderId) : null });
      toast.success(leaderId ? 'Chef de Famille désigné.' : 'Chef de Famille retiré.');
      fetchFamilies();
    } catch (err) {
      toast.error(err.response?.data?.leader?.[0] || err.response?.data?.leader || 'Erreur lors de la mise à jour.');
    }
  };

  const startEdit = (family) => {
    setEditingId(family.id);
    setName(family.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setName('');
  };

  const [nameError, setNameError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setNameError(requiredMessage('Nom de la famille')); return; }
    setNameError('');
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/participants/families/${editingId}/`, { name });
        toast.success('Famille mise à jour.');
      } else {
        const payload = { name };
        if (canSwitchChurch) payload.church_id = churchId;
        await api.post('/participants/families/', payload);
        toast.success('Famille créée.');
      }
      setName('');
      setEditingId(null);
      fetchFamilies();
    } catch (err) {
      const errors = apiFieldErrors(err, { name: 'Nom de la famille' });
      setNameError(errors.name || '');
      toast.error(firstErrorMessage(errors, { name: 'Nom de la famille' }, "Erreur lors de l'enregistrement."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (family) => {
    const msg = family.member_count > 0
      ? `"${family.name}" a ${family.member_count} membre(s). Ils seront désaffectés (aucun membre ne sera supprimé). Continuer ?`
      : `Supprimer la famille "${family.name}" ?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/participants/families/${family.id}/`);
      setFamilies(prev => prev.filter(f => f.id !== family.id));
      toast.success('Famille supprimée.');
    } catch {
      toast.error('Erreur lors de la suppression.');
    }
  };

  return (
    <div style={{ width: '100%', maxWidth: '760px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Familles</h1>
        <p style={{ color: 'var(--text-secondary)', marginTop: '4px', fontSize: '0.9rem' }}>
          Gérez les familles de l'église et consultez leurs membres.
        </p>
      </div>

      {isGlobal && (
        <div style={CARD}>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: '0 0 14px' }}>
            Vue globale — toutes les familles de toutes les églises (lecture seule). Sélectionnez une église pour les gérer.
          </p>
          {loading ? (
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Chargement...</p>
          ) : families.length === 0 ? (
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Aucune famille configurée.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
              {families.map(family => (
                <button key={family.id} onClick={() => setViewingFamily(family)}
                  style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '14px 16px', background: '#fff', textAlign: 'left', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <ChurchBadge name={family.church_name} />
                  <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>{family.name}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                    {family.member_count} membre{family.member_count > 1 ? 's' : ''} · Consulter →
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {canSwitchChurch && !churchId && !isGlobal && (
        <div style={CARD}>
          <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', margin: 0 }}>
            Sélectionnez une église dans la barre du haut pour gérer ses familles.
          </p>
        </div>
      )}

      {churchId && useFamilies === false && (
        <div style={{ ...CARD, background: '#fef9c3', borderColor: '#fef08a' }}>
          <p style={{ fontSize: '0.88rem', color: '#854d0e', margin: 0 }}>
            La fonctionnalité "Familles" n'est pas activée pour cette église. Active-la depuis
            {isPlatformAdmin ? ' Gestion des églises (modifier l\'église)' : ' Paramètres du site'} pour pouvoir créer des familles.
          </p>
        </div>
      )}

      {churchId && useFamilies && (
        <div style={CARD}>
          <label style={{ ...LABEL, marginBottom: '6px' }}>Nom de la famille<Req /></label>
          <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', gap: '10px', marginBottom: '18px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <input value={name} onChange={e => setName(e.target.value)}
                placeholder="Ex : Famille Grâce"
                style={{ ...INPUT, borderColor: nameError ? ERROR_COLOR : undefined }} />
              <FieldError message={nameError} />
            </div>
            <button type="submit" disabled={saving}
              style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 700, fontSize: '0.85rem', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? '...' : (editingId ? 'Enregistrer' : '+ Ajouter')}
            </button>
            {editingId && (
              <button type="button" onClick={cancelEdit}
                style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '10px 16px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
                Annuler
              </button>
            )}
          </form>

          {loading ? (
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Chargement...</p>
          ) : families.length === 0 ? (
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Aucune famille configurée.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
              {families.map(family => (
                <div key={family.id} style={{
                  border: '1px solid var(--border)', borderRadius: '10px', padding: '14px 16px',
                  display: 'flex', flexDirection: 'column', gap: '10px', background: '#fff',
                }}>
                  <button onClick={() => setViewingFamily(family)}
                    style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer' }}>
                    <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>{family.name}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                      {family.member_count} membre{family.member_count > 1 ? 's' : ''} · Consulter →
                    </div>
                  </button>
                  <select value={family.leader || ''} onChange={e => setLeader(family, e.target.value)}
                    title={leaders.length === 0 ? "Créez d'abord un utilisateur avec le rôle « Chef de Famille »" : 'Chef de Famille'}
                    style={{ ...INPUT, padding: '6px 8px', fontSize: '0.78rem' }}>
                    <option value="">{leaders.length === 0 ? 'Aucun compte Chef de Famille' : '— Chef de Famille —'}</option>
                    {leaders.map(u => (
                      <option key={u.id} value={u.id}>{[u.first_name, u.last_name].filter(Boolean).join(' ') || u.username}</option>
                    ))}
                  </select>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => startEdit(family)}
                      style={{ background: '#eff6ff', color: '#1e40af', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: '0.76rem', fontWeight: 700, cursor: 'pointer', flex: 1 }}>
                      Modifier
                    </button>
                    <button onClick={() => handleDelete(family)}
                      style={{ background: '#fef2f2', color: '#b91c1c', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: '0.76rem', fontWeight: 700, cursor: 'pointer', flex: 1 }}>
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {churchId && useFamilies && <FamilyStatsPanel churchId={churchId} />}

      {viewingFamily && (
        <FamilyMembersModal
          family={viewingFamily}
          churchId={churchId}
          onClose={() => setViewingFamily(null)}
          onChanged={fetchFamilies}
        />
      )}
    </div>
  );
}
